import { useProcessField } from '@shared/hooks/useProcessField';
import { useQuerySuspenseFetch } from '@shared/hooks/useQuerySuspenseFetch';
import {
    ProcessField,
    ProcessFieldRaw,
    ServiceVM,
} from '@shared/types/process';
import { parseFieldDefaultValue } from '@shared/util/flowParseValue';

import { ProcessFieldDynamic } from './ProcessFieldDynamic';

import { ShadcnForm } from '@shadcn-features/core';
import { useMemo } from 'react';

type ProcessFieldDynamicProps = React.ComponentPropsWithoutRef<
    typeof ProcessFieldDynamic
>;

type Props = Omit<ProcessFieldDynamicProps, 'field' | 'name'> & {
    field?: ProcessField;
    processFieldId: string;
    isViewHaveValue?: boolean;
    name?: string;
    service?: ServiceVM;
};

function ProcessFieldDynamicDisplayInner({
    field,
    isViewHaveValue,
    name,
    ...props
}: Omit<Props, 'processFieldId'> & { field: ProcessField }) {
    const form = ShadcnForm.useFormInstance();

    const ProcessFieldDynamicComponent =
        ProcessFieldDynamic as React.ComponentType<
            Omit<ProcessFieldDynamicProps, 'field'> & {
                field: ProcessField;
            }
        >;

    const dynamicProps = {
        ...props,
        name: name ?? field.id,
    } as Omit<ProcessFieldDynamicProps, 'field'>;

    const isHaveValue = useMemo(() => {
        if (!isViewHaveValue) return true;

        const data = form.getValues(field.id);
        if (data) return true;
        return false;
    }, [field.id, form, isViewHaveValue]);

    if (!isHaveValue) return null;

    return <ProcessFieldDynamicComponent field={field} {...dynamicProps} />;
}

function ProcessFieldDynamicDisplayFetch(props: Props) {
    const { fieldId } = useProcessField(props.processFieldId);
    const { processFieldId: _processFieldId, ...innerProps } = props;

    const { data: field } = useQuerySuspenseFetch<
        ProcessFieldRaw,
        ProcessField
    >(`flow/ServiceProcessFields/Get/${fieldId}`, {
        select: (rawField) => {
            const fieldParsed: ProcessField = {
                ...rawField,
                options: JSON.parse(rawField.options || '{}'),
            };

            return {
                ...fieldParsed,
                defaultValue: parseFieldDefaultValue(fieldParsed),
            };
        },
    });

    return <ProcessFieldDynamicDisplayInner {...innerProps} field={field} />;
}

export function ProcessFieldDynamicDisplay(props: Props) {
    if (props.field) {
        const { processFieldId: _processFieldId, ...innerProps } = props;

        return (
            <ProcessFieldDynamicDisplayInner
                {...innerProps}
                field={props.field}
            />
        );
    }

    return <ProcessFieldDynamicDisplayFetch {...props} />;
}

export default ProcessFieldDynamicDisplay;
